import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { authApi } from "../utils/api";
import type { AuthResponse, User } from "../server/auth/types";

interface AuthContextType {
  user: Omit<User, "hashedPassword" | "password"> | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (
    credentials: AuthResponse | { email: string; password: string }
  ) => Promise<void>;
  signup: (credentials: {
    email: string;
    name: string;
    password: string;
    confirmPassword: string;
  }) => Promise<void>;
  loginWithGoogle: (credentialResponse: string) => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<Omit<
    User,
    "hashedPassword" | "password"
  > | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch user profile with token - defined before it's used in useEffect
  const fetchUserProfile = useCallback(async (authToken: string) => {
    try {
      setIsLoading(true);
      const userData = await authApi.getProfile();
      setUser(userData);
    } catch (err) {
      console.error("Failed to fetch user profile:", err);
      // Token might be invalid, clear it
      localStorage.removeItem("token");
      setToken(null);
      setError("Session expired. Please login again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check for existing token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      setToken(storedToken);
      fetchUserProfile(storedToken);
    } else {
      setIsLoading(false);
    }
  }, [fetchUserProfile]);

  // Login function that can accept either an AuthResponse or email/password
  const login = useCallback(
    async (credentials: AuthResponse | { email: string; password: string }) => {
      try {
        setIsLoading(true);
        setError(null);

        let response: AuthResponse;

        // Check if credentials is an AuthResponse or email/password
        if ("token" in credentials && "user" in credentials) {
          // It's already an AuthResponse
          response = credentials as AuthResponse;
        } else {
          // It's email/password, call the API
          const { email, password } = credentials;
          response = await authApi.login(email, password);
        }

        // Save token and user data
        localStorage.setItem("token", response.token);
        setToken(response.token);
        setUser(response.user);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Login failed. Please check your credentials."
        );
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Signup function (alias for register to maintain backward compatibility)
  const signup = useCallback(
    async (data: {
      email: string;
      name: string;
      password: string;
      confirmPassword: string;
    }) => {
      try {
        setIsLoading(true);
        setError(null);
        console.log("Starting signup process for:", data.email);

        const response = await authApi.register(data);
        console.log(
          "Signup successful, received token:",
          response.token ? "Token exists" : "No token"
        );

        // Save token and user data
        localStorage.setItem("token", response.token);
        console.log("Token saved to localStorage");
        setToken(response.token);
        setUser(response.user);
      } catch (err) {
        console.error("Signup failed:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Registration failed. Please try again."
        );
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Update profile function
  const updateProfile = useCallback(async (updates: Partial<User>) => {
    try {
      setIsLoading(true);
      setError(null);

      const updatedUser = await authApi.updateProfile(updates);
      setUser(updatedUser);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update profile."
      );
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Google login function
  const loginWithGoogle = useCallback(async (credentialResponse: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Parse the JWT to get user information
      const payload = parseJwt(credentialResponse);

      // Send the token to our backend for verification and user creation/login
      const googleData = {
        token: credentialResponse,
        email: payload.email,
        name: payload.name,
        picture: payload.picture,
      };

      const response = await authApi.googleAuth(googleData);

      // Save token and user data
      localStorage.setItem("token", response.token);
      setToken(response.token);
      setUser(response.user);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Google login failed. Please try again."
      );
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Helper function to parse JWT token
  const parseJwt = (token: string) => {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch (e) {
      return null;
    }
  };

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  }, []);

  // Set error function for backward compatibility
  const setErrorWrapper = useCallback((newError: string | null) => {
    setError(newError);
  }, []);

  // Set loading function for backward compatibility
  const setLoadingWrapper = useCallback((newLoading: boolean) => {
    setIsLoading(newLoading);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user,
        isLoading,
        error,
        login,
        signup,
        loginWithGoogle,
        updateProfile,
        setError: setErrorWrapper,
        setLoading: setLoadingWrapper,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
